import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "@/components/Table/Table";
import Pagination from "@/components/Table/Pagination";
import type { Column } from "@/types/common";
import userService from "@/services/api/user";
import type { User } from "@/services/api/user";
import { USER_TYPE_OPTIONS } from "@/services/api/user";
import { combineClasses, humanizeEnumValue } from "@/lib/utils";
import { getDisplayName, getInitials } from "@/lib/user";
import { formatMemberSubtitle, formatRoom } from "./lib/memberFormat";
import { getMemberStatusTone } from "@/lib/statusTone";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getUsers } from "@/store/slices/userSlice";
import routes from "@/routes/routes";
import ExportConfirmationModal from "@/components/ExportConfirmationModal/ExportConfirmationModal";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import { StatCardsRow } from "@/components/StatCard/StatCard";
import type { StatCardData } from "@/components/StatCard/StatCard";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import Eye from "@/components/SVGComponents/Eye";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import CheckIcon from "@/components/SVGComponents/CheckIcon";
import SearchIcon from "@/components/SVGComponents/SearchIcon";
import ChevronDownIcon from "@/components/SVGComponents/ChevronDownIcon";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import Button from "@/components/Button/Button";
import { TableCellTooltip } from "@/components/Tooltip";
import toast from "@/lib/toast";
import AddMemberModal from "./components/AddMemberModal";
import UnblockMemberModal from "./components/UnblockMemberModal";
import MemberCard from "./components/MemberCard";
import styles from "./Members.module.scss";

// Shared with the export modal so its "everything"/"filtered" row-count
// queries hit the same scope as the table itself. `usertype` dropped per
// the user — was filtering to "FOUNDER" only, excluding real members of
// other types from this list.
const MEMBER_QUERY_BASE = { screenType: "management", renewalDays: 30 };

// Real data — GET /users through `userSlice`/`services/api/user.ts`. The
// exact response field names are the best available knowledge of this
// backend's user model, not verified against a live authenticated
// response (the endpoint requires auth; see docs/decisions.md). If the
// columns below come back blank/mismatched once a real session hits this,
// that's a field-name mismatch to fix in `services/api/user.ts`, not a
// bug in this page.
// Values match the real `UserStatus` enum given directly by the user
// (see services/api/user.ts) — "BLOCK", not "BLOCKED" as first guessed.
const STATUS_OPTIONS = [
  { label: "Approved", value: "APPROVED" },
  { label: "Blocked", value: "BLOCK" },
];

// "View record" navigates the same place a row click does (see the
// table's own `onRowClicked`). The second action toggles on status: a
// blocked member gets "Unblock" (PATCH status APPROVED), everyone else
// gets "Block" (PATCH status BLOCK) — both hit the real
// `PATCH /users/{id}/status` endpoint behind a confirm modal.
const getRowActions = (
  user: User,
  onViewClick: (user: User) => void,
  onBlockClick: (user: User) => void,
  onUnblockClick: (user: User) => void,
): RowAction[] => {
  const actions: RowAction[] = [
    {
      key: "view",
      label: "View record",
      icon: <Eye width={16} height={16} strokeWidth={1.75} />,
      onClick: () => onViewClick(user),
    },
  ];

  if (user.status === "BLOCK") {
    actions.push({
      key: "unblock",
      label: "Unblock",
      icon: <CheckIcon width={14} height={14} />,
      onClick: () => onUnblockClick(user),
    });
  } else {
    actions.push({
      key: "block",
      label: "Block",
      icon: <CrossIcon withBg={false} stroke="currentColor" strokeWidth={1.5} width={14} height={14} />,
      tone: "danger",
      onClick: () => onBlockClick(user),
    });
  }

  return actions;
};

const Members = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { users, totalUsers, approvedCount, subscriptionRenewalsCount, isFetching } = useAppSelector(
    (state) => state.user,
  );

  const memberStats: StatCardData[] = useMemo(
    () => [
      { label: "Joined this month", value: approvedCount },
      { label: "Renewals due in 30 days", value: subscriptionRenewalsCount },
    ],
    [approvedCount, subscriptionRenewalsCount],
  );

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [pageSelected, setPageSelected] = useState(1);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [userToBlock, setUserToBlock] = useState<User | null>(null);
  const [userToUnblock, setUserToUnblock] = useState<User | null>(null);
  const rowsPerPageValue = 10;

  const formConfig = useMemo(
    () => ({
      status: {
        controlType: "select" as const,
        label: "Status",
        isControlled: true,
        props: { options: STATUS_OPTIONS },
      },
      role: {
        controlType: "select" as const,
        label: "Role",
        isControlled: true,
        props: { options: USER_TYPE_OPTIONS },
      },
    }),
    [],
  );

  const fetchMembers = useCallback(() => {
    dispatch(
      getUsers({
        search: debouncedSearch || undefined,
        pageNo: pageSelected,
        perPage: rowsPerPageValue,
        ...MEMBER_QUERY_BASE,
        status: statusFilter || undefined,
        userType: roleFilter || undefined,
      }),
    );
  }, [dispatch, debouncedSearch, pageSelected, statusFilter, roleFilter]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const goToMemberDetails = (row: User) =>
    // The clicked row is passed via router state (same convention as
    // Waitlist.tsx → ApplicationReview.tsx) — no GET-by-id endpoint
    // exists, and MemberDetails' own page-1 lookup can miss this member.
    navigate(routes.privateRoutes.MEMBER_DETAILS.path.replace(":memberId", String(row.id)), {
      state: row,
    });

  const handleConfirmBlock = async () => {
    if (!userToBlock) return;
    const name = getDisplayName(userToBlock);
    try {
      await userService.blockUser(userToBlock.id);
      toast.success(`${name} has been blocked.`);
      fetchMembers();
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  };

  usePageSubtitle(isFetching ? "Loading members…" : `${totalUsers} members`);

  const columns: Column<User>[] = [
    {
      name: "Member",
      width: "25%",
      cell: (row) => (
        <div className={styles.memberCell} data-tag="allowRowEvents">
          <span className={styles.avatar}>{getInitials(getDisplayName(row))}</span>
          <div className={styles.memberDetails}>
            <TableCellTooltip value={getDisplayName(row)} textClassName={styles.memberName} />
            <TableCellTooltip value={formatMemberSubtitle(row)} textClassName={styles.memberSubtitle} />
          </div>
        </div>
      ),
    },
    {
      name: "Email",
      width: "20%",
      cell: (row) => <TableCellTooltip value={row.email || "—"} />,
    },
    {
      name: "Room",
      width: "16%",
      center: true,
      cell: (row) => {
        const room = formatRoom(row.userCategory);
        return room ? (
          <TableCellTooltip value={room || "—"} />
          // <StatusBadge label={room} tone="neutral" className={styles.roomBadge} />
        ) : (
          <span>—</span>
        );
      },
    },
    {
      name: "City",
      width: "16%",
      selector: (row) => row.cityName || row.location || "—",
    },
    {
      name: "Status",
      width: "15%",
      cell: (row) => (
        <StatusBadge
          label={row.status ? humanizeEnumValue(row.status) : "—"}
          tone={getMemberStatusTone(row.status)}
        />
      ),
    },
    {
      name: "Action",
      width: "8%",
      ignoreRowClick: true,
      cell: (row) => (
        <RowActionsMenu
          actions={getRowActions(row, goToMemberDetails, setUserToBlock, setUserToUnblock)}
          ariaLabel={`Actions for ${getDisplayName(row)}`}
        />
      ),
    },
  ];

  const handleTableClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    if (target.getAttribute("data-tag")) return;

    const isInteractive = target.closest(
      "button, a, input, select, textarea, [role='button'], [data-ignore-row-click='true']",
    );
    if (isInteractive) return;

    const cell = target.closest<HTMLElement>(".rdt_TableCell");
    if (cell && !cell.getAttribute("data-tag")) {
      return;
    }

    target.setAttribute("data-tag", "allowRowEvents");
  };

  return (
    <>
      <StatCardsRow stats={memberStats} />

      {/* Desktop View: 100% untouched existing implementation */}
      <div className={styles.desktopView} onClickCapture={handleTableClickCapture}>
        <Table<User>
          columns={columns}
          data={users}
          progressPending={isFetching}
          // containerHeight="calc(100vh - 210px)"
          pointerOnHover
          onRowClicked={goToMemberDetails}
          header={{
            // No `titleName` here — PostLogin's Topbar already shows the page
            // title (derived from `routes.privateRoutes.MEMBERS.pageName`);
            // repeating it as a second heading inside the table card would be
            // redundant, per the target design.
            search,
            setSearch,
            searchPlaceholder: "Search members",
            formConfig,
            onFilterSubmit: (data) => {
              setStatusFilter((data.status as string | undefined) ?? null);
              setRoleFilter((data.role as string | undefined) ?? null);
              setPageSelected(1);
            },
            addButtonTitle: "ADD MEMBER",
            onAddClick: () => setIsAddMemberModalOpen(true),
            onExport: () => setIsExportModalOpen(true),
          }}
          paginationProps={{
            isPagination: true,
            pageSelected,
            totalCount: totalUsers,
            rowsPerPageValue,
            setPageSelected,
          }}
        />
      </div>

      {/* Mobile View: responsive card list & controls */}
      <div className={styles.mobileView}>
        {/* Mobile Search */}
        <div className={styles.mobileSearchBox}>
          <SearchIcon className={styles.mobileSearchIcon} />
          <input
            type="text"
            placeholder="Search members"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <CrossIcon
              onClick={() => setSearch("")}
              withBg={false}
              height={12}
              width={12}
              strokeWidth={2}
              className={styles.mobileCrossIcon}
            />
          )}
        </div>

        {/* Mobile Controls: Status Filter and Action Buttons */}
        <div className={styles.mobileControlsRow}>
          <div className={styles.mobileFilterWrapper}>
            <select
              value={statusFilter || ""}
              onChange={(e) => {
                setStatusFilter(e.target.value || null);
                setPageSelected(1);
              }}
              aria-label="Filter by status"
            >
              <option value="">Status: All</option>
              <option value="APPROVED">Status: Approved</option>
              <option value="BLOCK">Status: Blocked</option>
            </select>
            <ChevronDownIcon className={styles.selectChevron} />
          </div>

          <div className={styles.mobileFilterWrapper}>
            <select
              value={roleFilter || ""}
              onChange={(e) => {
                setRoleFilter(e.target.value || null);
                setPageSelected(1);
              }}
              aria-label="Filter by role"
            >
              <option value="">Role: All</option>
              {USER_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  Role: {option.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon className={styles.selectChevron} />
          </div>

          <div className={styles.mobileActions}>
            <Button
              outlined
              onClick={() => setIsExportModalOpen(true)}
              className={combineClasses(styles.mobileActionBtn, styles.mobileExportBtn)}
            >
              <DownloadIcon className={styles.actionBtnIcon} />
              <span>EXPORT</span>
            </Button>
            <Button
              onClick={() => setIsAddMemberModalOpen(true)}
              className={combineClasses(styles.mobileActionBtn, styles.mobileAddBtn)}
            >
              <PlusIcon className={styles.actionBtnIcon} />
              <span>ADD</span>
            </Button>
          </div>
        </div>

        {/* Mobile Member Cards List */}
        <div className={styles.mobileCardList}>
          {isFetching ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className={styles.mobileSkeletonCard}>
                <div className={styles.skeletonHeader}>
                  <div className={styles.skeletonAvatar} />
                  <div className={styles.skeletonTextGroup}>
                    <div className={combineClasses(styles.skeletonLine, styles.short)} />
                    <div className={combineClasses(styles.skeletonLine, styles.long)} />
                  </div>
                </div>
                <div className={styles.skeletonGrid}>
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} />
                </div>
              </div>
            ))
          ) : users.length === 0 ? (
            <div className={styles.mobileEmptyState}>
              <h4 className={styles.mobileEmptyTitle}>No members found</h4>
              <p className={styles.mobileEmptyCaption}>Try clearing your search or filters.</p>
            </div>
          ) : (
            users.map((user) => (
              <MemberCard
                key={user.id}
                user={user}
                onCardClick={goToMemberDetails}
                onBlockClick={setUserToBlock}
                onUnblockClick={setUserToUnblock}
              />
            ))
          )}
        </div>

        {/* Mobile Pagination */}
        {totalUsers > rowsPerPageValue && (
          <div className={styles.mobilePaginationContainer}>
            <Pagination
              pageSelected={pageSelected}
              totalCount={totalUsers}
              rowsPerPageValue={rowsPerPageValue}
              setPageSelected={setPageSelected}
            />
          </div>
        )}
      </div>

      <ExportConfirmationModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        fallbackFileName="members"
        fetchExportFile={() =>
          userService.exportUsers({
            ...MEMBER_QUERY_BASE,
            search: debouncedSearch || undefined,
            status: statusFilter || undefined,
            userType: roleFilter || undefined,
          })
        }
      />
      <AddMemberModal
        isOpen={isAddMemberModalOpen}
        onClose={() => setIsAddMemberModalOpen(false)}
        onCreated={fetchMembers}
      />
      <ConfirmDeleteModal
        isOpen={userToBlock !== null}
        onClose={() => setUserToBlock(null)}
        onConfirm={handleConfirmBlock}
        headerTitle="Confirm Block"
        confirmLabel="Block"
        title="Block Member"
        message={
          userToBlock ? (
            <>
              Are you sure you want to block <strong>{getDisplayName(userToBlock)}</strong>? They will lose access to
              their account.
            </>
          ) : null
        }
      />
      <UnblockMemberModal
        user={userToUnblock}
        onClose={() => setUserToUnblock(null)}
        onUnblocked={fetchMembers}
      />
    </>
  );
};

export default Members;
